<template>
  <Modal
    ref="modal"
    form
    :full-height="$isMobile"
    offset-menu
    :title="$t('modals.request_transcript_item_modal.title')"
    @submit="confirmRequest"
  >
    <div class="field-heading q-mb-sm">
      {{ mentionType }}
    </div>
    <template v-if="mentionSyndications.length">
      <InputSelect
        v-model="syndicationId"
        emit-value
        new-design
        option-label="label"
        option-value="id"
        :options="mentionSyndications"
      />
      <div class="field-subtext q-mt-sm">
        {{
          $t(
            "modals.request_transcript_item_modal.select_syndication_description"
          )
        }}
      </div>
    </template>
    <div v-else>{{ mention.source.name }} {{ mention.source.location }}</div>

    <template v-if="mention.program_airing">
      <div class="field-heading q-mb-sm q-mt-lg">
        {{ $t("modals.request_transcript_item_modal.program_heading") }}
      </div>
      <div>
        {{ mention.program_airing.name }}
      </div>
    </template>

    <template v-if="mentionTypePodcast">
      <div v-if="podcastDurationSecond > 1">
        <div class="field-heading q-mb-sm q-mt-lg">
          {{
            $t("modals.request_transcript_item_modal.episode_duration_heading")
          }}
        </div>
        <div>{{ formattedPodcastDuration }}</div>
      </div>
      <div>
        <div class="field-heading q-mb-sm q-mt-lg">
          {{ $t("modals.request_transcript_item_modal.released_heading") }}
        </div>
        <TimeAgo
          :date="selectedMention.timestamp"
          :timezone="selectedMention.source.time_zone"
        />
      </div>
    </template>
    <div v-else>
      <div class="field-heading q-mb-sm q-mt-lg">
        {{ $t("modals.request_transcript_item_modal.starting_time_heading") }}
      </div>
      <div>{{ dateYmdhms(mentionStartTime) }}</div>
    </div>

    <div class="q-mt-lg">
      <div class="field-heading q-mb-sm">
        {{
          $t(
            "modals.request_transcript_item_modal.additional_recipients_heading"
          )
        }}
      </div>
      <DistributedEmailInput
        v-model="recipients"
        class="q-mb-md"
        new-design
      />
    </div>
    <!-- Context and Duration textarea -->
    <InputText
      v-model="form.contextDuration.value"
      class="q-mt-lg"
      :height="80"
      :label="form.contextDuration.label"
      new-design
      :type="form.contextDuration.type"
      :validations="['notBlank']"
    />
    <div class="field-subtext q-mt-sm">
      {{ form.contextDuration.placeholder }}
    </div>
    <div class="soft smaller q-pt-lg column q-gutter-md">
      <div class="text-danger">
        {{
          $t("modals.request_transcript_item_modal.understand_text", {
            organisationName: organisation.name,
          })
        }}
      </div>
      <div>
        {{ $t("modals.request_transcript_item_modal.process_text") }}
      </div>
      <div>
        {{
          $t("modals.request_transcript_item_modal.effort_text", {
            platformName: appConfig.platformName,
          })
        }}
      </div>
    </div>

    <template #buttons>
      <BaseButton
        :label="$t('global.cancel')"
        new-design
        @click="$modals.close('RequestTranscriptItemModal')"
      />
      <BaseButton
        color="primary"
        :label="$t('modals.request_transcript_item_modal.confirm_button')"
        new-design
        type="submit"
      />
    </template>
  </Modal>
</template>

<script>
import { zonedTimeToUtc } from "date-fns-tz";
import { storeToRefs } from "pinia";

import { BaseButton, InputSelect, InputText } from "shared/components/base";
import DistributedEmailInput from "shared/components/core/forms/DistributedEmailInput.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import useTeamPicker from "shared/composables/useTeamPicker";
import {
  addTimeZone,
  dateYmdhms,
  duration,
  unixDateTime,
} from "shared/helpers/date";
import { TranscriptRequest } from "shared/resources";
import { useGlobalStore } from "shared/stores/global";
import { useOrganisationFeaturesStore } from "shared/stores/organisationFeatures";
import { useUserStore } from "shared/stores/user";

export default {
  name: "RequestTranscriptItemModal",
  components: {
    DistributedEmailInput,
    Modal,
    BaseButton,
    InputSelect,
    InputText,
  },
  props: {
    mention: {
      type: Object,
      required: true,
    },
    origin: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    podcastEpisodeDuration: {
      type: Number,
      default: 0,
    },
  },
  setup() {
    const { refreshUsedCredits } = useOrganisationFeaturesStore();

    const userStore = useUserStore();
    const { currentUser, organisation } = storeToRefs(userStore);

    const globalStore = useGlobalStore();
    const { appConfig } = storeToRefs(globalStore);

    const { selectedTeamPreference } = useTeamPicker();

    return {
      refreshUsedCredits,
      currentUser,
      organisation,
      appConfig,
      selectedTeamPreference,
    };
  },
  data() {
    return {
      form: {
        contextDuration: {
          type: "textarea",
          label: this.$t(
            "modals.request_transcript_item_modal.context_duration_label"
          ),
          placeholder: this.$t(
            "modals.request_transcript_item_modal.context_duration_label_description",
            { platformName: this.appConfig.platformName }
          ),
          value: "",
        },
      },
      recipients: [],
      syndicationId: 0,
      syndication: {},
    };
  },
  computed: {
    syndicationsArePresent() {
      return Object.keys(this.syndication).length;
    },
    mentionSyndications() {
      if (this.mention.syndications && this.mention.syndications.length) {
        const syndications = this.mention.syndications.map((syndication) => ({
          label: syndication.source_name,
          value: syndication,
          id: syndication.id,
        }));

        syndications.unshift({
          label: this.mention.source.name,
          value: this.mention,
          id: this.mention.id,
        });

        return syndications;
      }

      return [];
    },
    podcastDurationSecond() {
      return Math.floor(this.podcastEpisodeDuration);
    },
    formattedPodcastDuration() {
      return duration(
        this.selectedMention.timestamp + this.podcastDurationSecond,
        this.selectedMention.timestamp
      );
    },
    mentionTypePodcast() {
      return this.mention.type === "podcast_episode";
    },
    mentionTypeRadio() {
      return this.mention.type === "radio_clip";
    },
    mentionType() {
      if (this.mentionTypePodcast) {
        return this.$t(
          "modals.request_transcript_item_modal.mention_type_podcast"
        );
      }

      if (this.mentionTypeRadio) {
        return this.$t(
          "modals.request_transcript_item_modal.mention_type_station"
        );
      }

      return this.$t(
        "modals.request_transcript_item_modal.mention_type_channel"
      );
    },
    mentionStartTime() {
      return this.syndicationsArePresent
        ? this.syndication.start_time
        : this.mention.start_time;
    },
    selectedMention() {
      return this.syndicationsArePresent ? this.syndication : this.mention;
    },
    sourceId() {
      if (this.syndicationsArePresent) {
        return this.syndication.source
          ? this.syndication.source.id
          : this.syndication.source_id;
      }

      return this.mention.source.id;
    },
    hasCredit() {
      return this.$features.hasCredits("audio_transcript");
    },
    reachedContractAllowance() {
      return this.$features.reachedContractAllowance("audio_transcript");
    },
    showConfirmDialog() {
      return (
        !this.hasCredit || (this.hasCredit && this.reachedContractAllowance)
      );
    },
    source() {
      const source = {
        sourceId: this.sourceId,
        sourceType: "TvChannel",
      };

      if (this.mentionTypePodcast) {
        source.sourceType = "Podcast";
      } else if (this.mentionTypeRadio) {
        source.sourceType = "RadioStation";
      }

      return source;
    },
    startTime() {
      if (this.mentionTypePodcast) {
        const dateInTimeZone = addTimeZone(
          this.selectedMention.timestamp,
          this.selectedMention.source.time_zone
        );

        const beginningOfDay = zonedTimeToUtc(
          new Date(
            dateInTimeZone.getFullYear(),
            dateInTimeZone.getMonth(),
            dateInTimeZone.getDate(),
            0,
            0,
            0
          ),
          this.selectedMention.source.time_zone
        );

        return unixDateTime(beginningOfDay);
      }

      return this.selectedMention.start_time;
    },
    endTime() {
      if (this.mentionTypePodcast) {
        return this.startTime + this.podcastDurationSecond;
      }

      return this.selectedMention.end_time;
    },
  },
  watch: {
    syndicationId(value) {
      const syndication = this.mentionSyndications.find(
        (mentionSyndication) => mentionSyndication.id === value
      );

      if (syndication) {
        this.syndication = syndication.value;
      }
    },
  },
  async mounted() {
    this.syndication = this.mentionSyndications.length ? this.mention : {};

    if (this.syndication) {
      this.syndicationId = this.syndication.id;
    }

    await this.refreshUsedCredits({
      organisationId: this.organisation.id,
      feature: "audio_transcript",
    });
  },
  methods: {
    dateYmdhms,
    confirmRequest() {
      if (this.showConfirmDialog) {
        this.$modals.open("RequestMediaTranscriptItemConfirmModal", {
          props: {
            type: "audio_transcript",
          },
          events: {
            confirm: () => {
              this.send();
            },
          },
        });
      } else {
        this.send();
      }
    },
    async send() {
      this.$q.loading.show({ delay: 0 });

      try {
        const transcriptRequest = new TranscriptRequest({
          ...this.origin,
          ...this.source,
          billable: true,
          startTime: this.startTime,
          endTime: this.endTime,
          notes: this.form.contextDuration.value,
          timeZone: this.selectedMention.source.time_zone,
          recipients: this.recipients,
          instantDownload: false,
          notifyOnCompletion: true,
          formats: ["docx", "pdf"],
          userId: this.currentUser.id,
          fileUrl: [],
          podcastEpisodeId: this.mentionTypePodcast
            ? this.selectedMention.id
            : null,
          ...(this.$features.has("has_team_workspaces")
            ? { organisation_team_id: this.selectedTeamPreference }
            : {}),
        });

        await transcriptRequest.saveResource();

        this.$track("Requested Transcript", {
          mentionId: this.mention.id,
          mentionType: this.mention.type,
          sourceId: this.sourceId,
        });
      } finally {
        this.$q.loading.hide();
        this.closeModal();
      }

      this.$dialog
        .confirm({
          title: this.$t("modals.request_transcript_item_modal.thankyou_title"),
          message: this.$t(
            "modals.request_transcript_item_modal.thankyou_message"
          ),
          confirmLabel: this.$t("global.ok"),
          cancelLabel: this.$t(
            "modals.request_transcript_item_modal.thankyou_cancel_button"
          ),
          persistent: true,
          cancelButtonProps: {
            color: "primary",
          },
          dialogMaxWidth: "500px",
          theme: "danger",
        })
        .onCancel(() => {
          this.$track(
            "Clicked View Transcript Requests in Submitted Transcript Request Modal"
          );

          this.$safeRouterPush({
            name: "media-and-transcripts",
            query: {
              tab: "transcriptRequests",
            },
          });
        });
    },
    closeModal() {
      this.$refs.modal.close();
    },
  },
};
</script>
